import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import {
  collection,
  getDocs
} from 'firebase/firestore';

function Search() {
    // Company and year filter state and helpers
    const [allCompanies, setAllCompanies] = useState([]);
    const currentYear = new Date().getFullYear();
    const availableYears = Array.from({length: currentYear - 2017}, (_, i) => String(2018 + i));

    useEffect(() => {
      async function fetchCompanies() {
        try {
          const postsRef = collection(db, 'posts');
          const snap = await getDocs(postsRef);
          const companies = snap.docs
            .map(doc => doc.data().companyName || doc.data().company)
            .filter(Boolean);
          setAllCompanies(Array.from(new Set(companies)));
        } catch (e) {
          setAllCompanies([]);
        }
      }
      fetchCompanies();
    }, []);
  const MAX_QUERY_LENGTH = 200;

  const [queryText, setQueryText] = useState('');
  const [error, setError] = useState('');

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [lastTerm, setLastTerm] = useState('');

  const [selectedRounds, setSelectedRounds] = useState([]);
  const [selectedRatings, setSelectedRatings] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedYears, setSelectedYears] = useState([]);

  const [sortBy, setSortBy] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const availableRounds = ['1', '2', '3', '4', '5+'];
  const availableRatings = ['1', '2', '3', '4', '5'];

  const filterPost = (post) => {
    if (selectedRounds.length > 0 && !selectedRounds.includes(String(post.numRounds))) return false;
    if (selectedRatings.length > 0 && !selectedRatings.includes(String(post.rating))) return false;
    if (selectedCompanies.length > 0) {
      const company = post.companyName || post.company;
      if (!selectedCompanies.includes(company)) return false;
    }
    if (selectedYears.length > 0) {
      let year = post.year;
      if (!year && post.createdAt && post.createdAt.seconds) {
        year = String(new Date(post.createdAt.seconds * 1000).getFullYear());
      }
      if (!selectedYears.includes(year)) return false;
    }
    return true;
  };

  const sortPosts = (posts) => {
    if (sortBy === 'date') {
      return posts.slice().sort((a, b) => {
        const aDate = a.createdAt?.seconds || 0;
        const bDate = b.createdAt?.seconds || 0;
        return bDate - aDate;
      });
    } else if (sortBy === 'upvotes') {
      return posts.slice().sort((a, b) => (b.upvotes ?? 0) - (a.upvotes ?? 0));
    } else if (sortBy === 'lowupvotes') {
      return posts.slice().sort((a, b) => (a.upvotes ?? 0) - (b.upvotes ?? 0));
    }
    return posts;
  };

  // NEW helper: add commentCount (reply count) to each post (S7)
  const addCommentCounts = async (posts) => {
    const withCounts = await Promise.all(
      posts.map(async (post) => {
        try {
          const repliesRef = collection(db, 'posts', post.id, 'replies');
          const repliesSnap = await getDocs(repliesRef);
          return { ...post, commentCount: repliesSnap.size };
        } catch (e) {
          return { ...post, commentCount: 0 };
        }
      })
    );
    return withCounts;
  };

  const runSearch = async (term) => {
    const postsRef = collection(db, 'posts');
    let allPosts = [];
    try {
      const snap = await getDocs(postsRef);
      allPosts = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (e) {
      return [];
    }

    const lowerTerm = term.toLowerCase();
    let filtered = allPosts.filter(post =>
      (post.behavioralQuestions && post.behavioralQuestions.toLowerCase().includes(lowerTerm)) ||
      (post.technicalQuestions && post.technicalQuestions.toLowerCase().includes(lowerTerm)) ||
      (post.companyName && post.companyName.toLowerCase().includes(lowerTerm)) ||
      (post.roleName && post.roleName.toLowerCase().includes(lowerTerm))
    );

    filtered = filtered.filter(filterPost);

    // Add comment counts BEFORE sorting/rendering (S7)
    const withCounts = await addCommentCounts(filtered);

    return sortPosts(withCounts);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const term = queryText.trim();

    if (!term) {
      setError('Please enter a search term.');
      return;
    }
    if (term.length > MAX_QUERY_LENGTH) {
      setError('Shorten your search.');
      return;
    }

    setError('');
    setLoading(true);
    setHasSearched(true);
    setResults([]);
    setLastTerm(term);

    try {
      const posts = await runSearch(term);
      setResults(posts);
    } catch (err) {
      setError('Something went wrong while searching. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setQueryText(value);

    if (error) {
      const trimmed = value.trim();
      if (trimmed && trimmed.length <= MAX_QUERY_LENGTH) {
        setError('');
      }
    }
  };

  const handleClear = () => {
    setQueryText('');
    setError('');
    setResults([]);
    setHasSearched(false);
    setLastTerm('');
  };

  const handleCheckboxChange = (value, selectedArray, setSelectedArray) => {
    if (selectedArray.includes(value)) {
      setSelectedArray(selectedArray.filter(item => item !== value));
    } else {
      setSelectedArray([...selectedArray, value]);
    }
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
  };

  const toggleFilters = () => {
    setFiltersOpen(!filtersOpen);
  };

  const filteredSortedResults = sortPosts(results.filter(filterPost));

  return (
    <div className="page-content">
      <h1>Search Interview Posts</h1>

      <form onSubmit={handleSubmit} className="search-form">
        <label htmlFor="searchQuery">Search interview posts</label>

        <div className="search-row">
          <input
            id="searchQuery"
            type="text"
            value={queryText}
            onChange={handleChange}
            placeholder="e.g., onsite, behavioral, etc."
            aria-invalid={!!error}
            aria-describedby="searchError"
            disabled={loading}
          />

          <button type="submit" disabled={loading}>
            {loading ? 'Searching...' : 'Search'}
          </button>

          {(queryText || hasSearched) && (
            <button type="button" onClick={handleClear} disabled={loading}>
              Clear
            </button>
          )}
        </div>

        <p style={{ fontSize: '0.9rem', marginTop: '6px', color: '#666' }}>
          Max {MAX_QUERY_LENGTH} characters.
        </p>

        {error && (
          <p id="searchError" className="error-text">
            {error}
          </p>
        )}
      </form>

      <div className="filter-sort-bar">
        <div className="filter-dropdown-container">
          <button
            type="button"
            className="filter-toggle-btn"
            onClick={toggleFilters}
          >
            Filters {filtersOpen ? '▲' : '▼'}
          </button>

          {filtersOpen && (
            <div className="filters-dropdown">
              <div className="filter-section">
                <h4>Number of Rounds</h4>
                <div className="checkbox-group">
                  {availableRounds.map((round) => (
                    <label key={round} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedRounds.includes(round)}
                        onChange={() => handleCheckboxChange(round, selectedRounds, setSelectedRounds)}
                      />
                      {round}
                    </label>
                  ))}
                </div>
              </div>

              <div className="filter-section">
                <h4>Rating</h4>
                <div className="checkbox-group">
                  {availableRatings.map((rating) => (
                    <label key={rating} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedRatings.includes(rating)}
                        onChange={() => handleCheckboxChange(rating, selectedRatings, setSelectedRatings)}
                      />
                      {rating} Star{rating !== '1' ? 's' : ''}
                    </label>
                  ))}
                </div>
              </div>

              <div className="filter-section">
                <h4>Company</h4>
                <div className="checkbox-group">
                  {allCompanies.length === 0 ? (
                    <span style={{ color: '#888' }}>No companies found</span>
                  ) : (
                    allCompanies.map((company) => (
                      <label key={company} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectedCompanies.includes(company)}
                          onChange={() => handleCheckboxChange(company, selectedCompanies, setSelectedCompanies)}
                        />
                        {company}
                      </label>
                    ))
                  )}
                </div>
              </div>

              <div className="filter-section">
                <h4>Year</h4>
                <div className="checkbox-group">
                  {availableYears.map((year) => (
                    <label key={year} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedYears.includes(year)}
                        onChange={() => handleCheckboxChange(year, selectedYears, setSelectedYears)}
                      />
                      {year}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="sort-dropdown-container">
          <label htmlFor="sortBy">Sort by:</label>
          <select
            id="sortBy"
            value={sortBy}
            onChange={handleSortChange}
            className="sort-select"
          >
            <option value="">Select...</option>
            <option value="date">Date Posted</option>
            <option value="upvotes">Most Upvotes</option>
            <option value="lowupvotes">Lowest Upvotes</option>
          </select>
        </div>
      </div>

      <div className="search-results">
        {!hasSearched && <p>Enter a search term to see results.</p>}

        {hasSearched && loading && (
          <p>Searching{lastTerm ? ` for “${lastTerm}”` : ''}...</p>
        )}

        {hasSearched && !loading && filteredSortedResults.length > 0 && (
          <>
            <p>Found {filteredSortedResults.length} result(s).</p>

            <ul className="results-list">
              {filteredSortedResults.map((post) => (
                <li key={post.id} className="result-card">
                  <Link
                    to={`/post/${post.id}`}
                    state={{ from: '/search' }}
                    className="result-link"
                  >
                    <div className="result-title">
                      <strong>Post</strong>
                    </div>

                    <div className="result-meta">
                      <span><strong>Rounds:</strong> {post.numRounds || 'N/A'}</span>
                      <span className="meta-sep"> | </span>
                      <span><strong>Upvotes:</strong> {post.upvotes ?? 0}</span>
                      <span className="meta-sep"> | </span>
                      <span><strong>Comments:</strong> {post.commentCount ?? 0}</span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}

        {hasSearched && !loading && filteredSortedResults.length === 0 && (
          <div className="no-results">
            <p><strong>No matching posts were found.</strong></p>
            <p>Try a new search or post your own experience!</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Search;